import {
  FilterSortPopup,
  IStyles,
  IconWrapper,
  StudentScoreCard,
  firstLetterImage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import {
  SubmissionType,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../app/Context/DownloadContextProviderV2';
import { useGlobalContext } from '../../app/Context/GlobalContextProvider';
import { HorizontalFilterComponent } from '../HorizontalFilterComponent';
import { AssessmentPageType } from '../../pages/ManageHomework/ClickerAssessmentManage';
import { IStatusObj } from '../../pages/Assessment/ClickerAssessment/reducer/clickerAssessment.slice';
import { filterStudentsByPerformance } from '../../pages/ManageHomework/manageHWutils';
import { StudentPerformanceInfo } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { interactionEvent } from '../../pages/Auth/auth.events';
import { DownloadStudentResponseStatusButton } from '../../pages/ManageHomework/components/DownloadStudentResponseStatus';
import ViewBulkReportButton from '../../pages/ManageHomework/components/ViewBulkReportButton';

const styles: IStyles = {
  root: {
    width: { xs: '100%', md: '100%' },
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(30) },
  },
  header: {
    width: { xs: '94%', md: '100%', lg: '100%' },
    display: 'flex',
    marginLeft: { xs: '3%', md: 'auto' },
    gap: { xs: pxToRem(10), md: pxTovW(22) },
    justifyContent: 'space-between',
  },
  studentScoreCardGridBox: {
    width: { xs: '100%', md: '100%', lg: '100%' },
    paddingY: { xs: pxToRem(7), md: pxTovW(30) },
    paddingX: { xs: pxToRem(15), md: pxTovW(20) },
    boxShadow: `0px 0px ${pxToRem(13)} #E0DFDE`,
    backgroundColor: '#FFFFFF',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: { xs: pxToRem(20), md: pxTovW(30) },
    boxSizing: 'border-box',
  },
};

export interface ISubmissionProps {
  studentData?: StudentPerformanceInfo;
  marks?: number;
  checkedValue?: boolean;
}

interface IProps {
  className: string; // active
  studentPerformanceInfoList?: StudentPerformanceInfo[];
  cardMaxMarks?: number;
  cardSubmissionType?: SubmissionType;
  cardOnClickHandler?: (studentId: string) => void;
  checkBoxClickHandler?: (props: ISubmissionProps) => Promise<void>;
  saveStudentInfo?: (props: StudentPerformanceInfo) => void;
  statusInfo?: IStatusObj;
  textClickHandler?: (studentInfo: StudentPerformanceInfo) => void;
  taskType?: TaskEnum;
  pageType: AssessmentPageType;

  studentSubmissionCount?: number; // ended
  assignedStudentCount?: number; // ended
  isBestAttempt?: boolean;
  attemptNumber?: number;
  assessmentId?: bigint;
  showBulkReportButton?: boolean;
  assessmentName?: string;
  reportSubject?: string;
  reportClassName?: string;
  reportSection?: string;
  isSummaryReportDisabled?: boolean;
}
export const StudentPerformanceGrid = (props: IProps) => {
  const {
    className,
    studentPerformanceInfoList,
    cardSubmissionType,
    cardMaxMarks,
    cardOnClickHandler,
    checkBoxClickHandler,
    // saveStudentInfo,
    statusInfo,
    textClickHandler,
    taskType,
    pageType,
    studentSubmissionCount,
    assignedStudentCount,
    isBestAttempt,
    attemptNumber,
    assessmentId,
    showBulkReportButton,
  } = props;

  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const { isIpadOnly } = useGlobalContext();

  const [studentList, setStudentList] = useState<
    StudentPerformanceInfo[] | undefined
  >();
  const [selectedFilter, setSelectedFilter] = useState<string>('ALL');

  function splitName(fullName: string) {
    const nameParts = fullName.split(' ');
    const firstname = nameParts[0];
    const lastname = nameParts.slice(1).join(' '); // In case there are middle names or multiple last names
    return { firstname: firstname, lastname: lastname };
  }

  const SortFunction = async (value: string) => {
    const temp = filterStudentsByPerformance({
      filterType: selectedFilter,
      studentInfoList: studentPerformanceInfoList,
    });

    switch (value) {
      case 'Score: High to Low':
        temp?.sort((a, b) => b.scorePercent - a.scorePercent);
        setStudentList(temp);
        await interactionEvent({
          context: 'sort_by',
          name: 'SCORE_HIGH_TO_LOW',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
        break;
      case 'Score: Low to High':
        temp?.sort((a, b) => a.scorePercent - b.scorePercent);
        setStudentList(temp);
        await interactionEvent({
          context: 'sort_by',
          name: 'SCORE_LOW_TO_HIGH',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
        break;
      case 'First Name':
        temp?.sort((a, b) => {
          const FirstNameA = splitName(a.name).firstname;
          const FirstNameB = splitName(b.name).firstname;
          const nameA = FirstNameA.toLowerCase();
          const nameB = FirstNameB.toLowerCase();

          if (nameA < nameB) {
            return -1;
          } else if (nameA > nameB) {
            return 1;
          } else {
            return 0;
          }
        });
        setStudentList(temp);
        await interactionEvent({
          context: 'sort_by',
          name: 'FIRST_NAME',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
        break;
      case 'Last Name':
        temp?.sort((a, b) => {
          const LastNameA = splitName(a.name).lastname;
          const LastNameB = splitName(b.name).lastname;
          const nameA = LastNameA.toLowerCase();
          const nameB = LastNameB.toLowerCase();

          if (nameA < nameB) {
            return -1;
          } else if (nameA > nameB) {
            return 1;
          } else {
            return 0;
          }
        });
        setStudentList(temp);
        await interactionEvent({
          context: 'sort_by',
          name: 'LAST_NAME',
          pathSegments: pathSegments,
          isOffline: isOffline,
        });
        break;

      default:
        break;
    }
  };

  // const FilterFunction = (value: string) => {
  //   const temp = studentPerformanceInfoList;
  //   switch (value) {
  //     case 'High Score':
  //       setStudentList(temp?.filter((elem) => elem.scorePercent > 80));
  //       // console.log(temp?.filter((elem) => elem.scorePercent > 80));
  //       break;
  //     case 'Low Score':
  //       setStudentList(temp?.filter((elem) => elem.scorePercent < 30));
  //       break;
  //     case 'Medium Score':
  //       setStudentList(
  //         temp?.filter(
  //           (elem) => elem.scorePercent > 30 && elem.scorePercent < 80
  //         )
  //       );
  //       break;

  //     default:
  //       break;
  //   }
  // };
  const FilterFunction = (value: string) => {
    setStudentList(
      filterStudentsByPerformance({
        filterType: value,
        studentInfoList: studentPerformanceInfoList,
      })
    );
  };

  const getFilterData = (studentList: StudentPerformanceInfo[]) => {
    const totalStudents = studentList.length || 1; // Avoid division by zero

    const categories = {
      ALL: studentList,
      EXCELLENT: studentList.filter(
        (s) => s.isSubmitted === true && s.scorePercent >= 70 // equal to 70 is supossed to be handled here
      ),
      AVERAGE: studentList.filter(
        (s) =>
          s.isSubmitted === true && s.scorePercent >= 30 && s.scorePercent < 70
      ),
      LOW: studentList.filter(
        (s) => s.isSubmitted === true && s.scorePercent < 30
      ),
      NOT_SUBMITTED: studentList.filter((s) => s.isSubmitted === false),
    };

    return Object.entries(categories).map(([key, students]) => ({
      filter: key,
      value: students.length.toString(),
      percentage: parseFloat(
        ((students.length / totalStudents) * 100).toFixed(2)
      ),
    }));
  };

  // --------------------------------------------------------------

  useEffect(
    () => setStudentList(studentPerformanceInfoList),
    [studentPerformanceInfoList]
  );

  // --------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h2" fontWeight="medium">
            Students
          </Typography>

          {pageType === 'assessment-active' && (
            <Typography
              variant="cardText"
              fontWeight="bold"
              sx={{ color: '#007CDC' }}
            >
              Class {className}
            </Typography>
          )}

          {taskType !== TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM &&
            (pageType === 'connected-assess-ended' ||
              pageType === 'connected-assess-active' ||
              pageType === 'assessment-ended') && (
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: pxToRem(5), md: pxTovW(8) },
                  alignItems: 'center',
                }}
              >
                <IconWrapper
                  name="user"
                  size={isIpadOnly ? 'large' : 'md'}
                  parentFolder="icons"
                  type="png"
                />

                <Typography variant="h2" fontWeight="bold">
                  {studentSubmissionCount}/{assignedStudentCount}
                </Typography>
              </Box>
            )}
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: pxToRem(10), md: pxTovW(20) } }}>
          <FilterSortPopup
            iconName="Sort"
            title="Sort By"
            options={[
              'First Name',
              'Last Name',
              'Score: Low to High',
              'Score: High to Low',
            ]}
            sortFunction={SortFunction}
          />

          {/* <FilterSortPopup
            iconName="Filter"
            title="Filter"
            options={['High Score', 'Low Score', 'Medium Score']}
            filterFunction={FilterFunction}
          /> */}
        </Box>
      </Box>

      {taskType !== TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM &&
        (pageType === 'connected-assess-ended' ||
          pageType === 'assessment-ended') && (
          <HorizontalFilterComponent
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            filterData={
              studentPerformanceInfoList
                ? getFilterData(studentPerformanceInfoList)
                : []
            }
            handleFilterFunction={FilterFunction}
          />
        )}

      {studentList && studentList.length > 0 ? (
        <Box sx={styles.studentScoreCardGridBox}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: { xs: pxToRem(10), md: pxTovW(12) },
              width: { xs: '90vw', md: '100%' },
              flexWrap: 'wrap',
            }}
          >
            <DownloadStudentResponseStatusButton
              students={studentPerformanceInfoList}
              maxMarks={cardMaxMarks}
              assessmentName={props.assessmentName}
              subject={props.reportSubject}
              grade={props.reportClassName}
              section={props.reportSection}
            />
            {!!assessmentId && showBulkReportButton && (
              <ViewBulkReportButton
                isSummaryReportDisabled={props.isSummaryReportDisabled}
                assessmentId={assessmentId}
                isBestAttempt={isBestAttempt}
                attemptNumber={attemptNumber}
                rootStyles={{ width: 'auto' }}
                subject={props.reportSubject}
                className={props.reportClassName}
                section={props.reportSection}
              />
            )}
          </Box>
          {studentList?.map((elem, index) => (
            <Box
              onClick={() =>
                cardOnClickHandler && cardOnClickHandler(String(elem.studentId))
              }
              sx={{ cursor: 'pointer' }}
            >
              <StudentScoreCard
                studentName={elem.name}
                maxMarks={cardMaxMarks}
                submissionType={cardSubmissionType}
                submission={
                  (statusInfo &&
                    statusInfo[elem.studentId.toString()]?.isSubmitted) ||
                  false
                }
                score={Math.round(elem.scorePercent)}
                marks={
                  statusInfo && statusInfo[elem.studentId.toString()]?.marks
                }
                imageUrl={
                  getMediaBasePath(elem.profileImgUrl) ||
                  firstLetterImage(elem.name)
                }
                checkBoxClickHandler={checkBoxClickHandler}
                studentPerformanceInfo={elem}
                textClickHandler={textClickHandler}
                linearProgressStyles={{
                  borderRadius: { xs: pxToRem(4), md: pxTovW(10) },
                }}
                rootStyles={{
                  // width: { xs: '90vw', md: pxTovW(510), lg: pxTovW(340) },
                  width: { xs: 'inherit', md: pxTovW(450), lg: pxTovW(340) },
                }}
              />
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
